<template>
  <div class="uat-blocked-page min-h-screen w-full flex items-center justify-center p-4 sm:p-6 bg-slate-950 text-slate-100 font-sans select-none overflow-x-hidden">
    <!-- Ambient Background Glows -->
    <div class="fixed inset-0 overflow-hidden pointer-events-none z-0">
      <div class="absolute -top-32 -left-32 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl"></div>
      <div class="absolute -bottom-32 -right-32 w-96 h-96 bg-indigo-600/20 rounded-full blur-3xl"></div>
      <div class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[520px] h-[520px] bg-sky-500/10 rounded-full blur-3xl"></div>
    </div>

    <!-- Main Card Container -->
    <div class="relative z-10 w-full max-w-lg bg-slate-900/90 backdrop-blur-xl border border-slate-800/80 rounded-3xl p-6 sm:p-8 shadow-2xl text-center">
      
      <!-- Brand Logo / Badge -->
      <div class="w-full flex items-center justify-center mb-5">
        <img
          src="@/assets/img/CW_DEFAULT_WHITE.png"
          alt="Cuality Works Finfi"
          class="h-9 sm:h-10 w-auto object-contain opacity-95"
        />
      </div>

      <!-- Top Status Pill -->
      <div class="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 text-xs font-semibold uppercase tracking-wider mb-6">
        <span class="w-2 h-2 rounded-full bg-amber-400 animate-pulse"></span>
        UAT Admin Retired
      </div>

      <!-- Glowing Icon Graphic -->
      <div class="relative w-24 h-24 mx-auto mb-6 flex items-center justify-center">
        <div class="absolute inset-0 rounded-3xl bg-gradient-to-tr from-indigo-500 to-sky-400 opacity-20 blur-lg animate-pulse"></div>
        <div class="relative w-20 h-20 rounded-2xl bg-gradient-to-b from-slate-800 to-slate-900 border border-slate-700/60 shadow-xl flex items-center justify-center">
          <!-- Shield with External Arrow SVG -->
          <svg class="w-10 h-10 text-sky-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
            <path d="M10 12h4"></path>
            <path d="M12 10v4"></path>
          </svg>
        </div>
      </div>

      <!-- Headline -->
      <h1 class="text-2xl sm:text-3xl font-extrabold text-white tracking-tight mb-3">
        Use Production Admin
      </h1>

      <!-- Description -->
      <p class="text-sm text-slate-300 leading-relaxed mb-4">
        Access to the UAT Admin portal (<span class="text-amber-300 font-mono text-xs">uatadmin.myfinfi.com</span>) has been discontinued. All administrative and workforce tools are active on the official Production Admin portal.
      </p>

      <!-- Security / Guidance Information Callout -->
      <div class="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-4 text-xs text-slate-300 text-left mb-6 space-y-2.5">
        <div class="flex items-start gap-2.5">
          <svg class="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <span>Your session has been <strong class="text-emerald-300">safely logged out</strong> from this domain for security.</span>
        </div>
        <div class="flex items-start gap-2.5">
          <svg class="w-4 h-4 text-sky-400 mt-0.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"></circle>
            <path d="M12 16v-4"></path>
            <path d="M12 8h.01"></path>
          </svg>
          <span>Please sign in through our official production URL: <strong class="text-sky-300">admin.myfinfi.com</strong></span>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="flex flex-col items-center gap-3">
        <button
          type="button"
          @click="handleNavigate"
          class="group w-full px-6 py-3.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white rounded-2xl border border-blue-400/30 shadow-xl flex items-center justify-center gap-2.5 transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98] cursor-pointer font-semibold text-sm sm:text-base"
        >
          <span>Go to Production Admin</span>
          <svg class="w-4 h-4 text-white transform group-hover:translate-x-1 transition-transform" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="5" y1="12" x2="19" y2="12"></line>
            <polyline points="12 5 19 12 12 19"></polyline>
          </svg>
        </button>

        <!-- Copy Link Button -->
        <button
          type="button"
          @click="copyLink"
          class="text-xs text-slate-400 hover:text-slate-200 flex items-center gap-1.5 py-2 px-3 rounded-lg hover:bg-slate-800/40 transition-colors cursor-pointer bg-transparent border-none"
        >
          <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
            <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"></path>
          </svg>
          <span>{{ copied ? 'Production link copied to clipboard!' : 'Copy Production Admin link' }}</span>
        </button>
      </div>

      <!-- Plain Text URL Display (Fallback) -->
      <div class="mt-4 pt-4 border-t border-slate-800/80">
        <a
          :href="prodAdminUrl"
          class="text-[12px] text-sky-400 hover:text-sky-300 font-mono underline underline-offset-2 transition-colors break-all"
        >
          {{ prodAdminUrl }}
        </a>
      </div>

      <!-- Security / Safe Confirmation Footer -->
      <div class="mt-6 flex items-center justify-center gap-2 text-[11px] text-slate-500">
        <span class="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
        <span>Securely Disconnected &bull; Finfi Workforce Admin</span>
      </div>

    </div>
  </div>
</template>

<script>
import {
  PROD_ADMIN_URL,
  redirectToProdAdmin
} from '@/utils/enforceUatAdminDeprecation'

export default {
  name: 'UatAdminBlockedScreen',
  data() {
    return {
      prodAdminUrl: PROD_ADMIN_URL,
      copied: false
    }
  },
  methods: {
    handleNavigate() {
      redirectToProdAdmin()
    },
    async copyLink() {
      try {
        if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(this.prodAdminUrl)
          this.copied = true
          setTimeout(() => {
            this.copied = false
          }, 3000)
        } else {
          this.handleNavigate()
        }
      } catch (err) {
        this.handleNavigate()
      }
    }
  }
}
</script>

<style scoped>
.uat-blocked-page {
  position: fixed;
  inset: 0;
  z-index: 999999;
  background-color: #020617;
}
</style>
